import { StateCreator } from 'zustand'
import { StoreState } from './index'
import { CardAuthTokens } from '@/features/card/types/CardState'

export interface PerAddressAuthData {
  accessToken: string | null
  expiresIn: number | null
  tokenType: string | null
  hasRefreshSession: boolean
}

const emptyAddressData: PerAddressAuthData = {
  accessToken: null,
  expiresIn: null,
  tokenType: null,
  hasRefreshSession: false,
}

export interface CardAuthState {
  activeAddress: string | null
  byAddress: Record<string, PerAddressAuthData>
  accessToken: string | null
  expiresIn: number | null
  tokenType: string | null
  hasRefreshSession: boolean
}

export function isCardAuthValid(data: Pick<PerAddressAuthData, 'accessToken' | 'expiresIn'>): boolean {
  return !!(data.accessToken && data.expiresIn && data.expiresIn > Date.now())
}

function flatFieldsFrom(
  data: PerAddressAuthData,
): Pick<CardAuthState, 'accessToken' | 'expiresIn' | 'tokenType' | 'hasRefreshSession'> {
  return {
    accessToken: data.accessToken,
    expiresIn: data.expiresIn,
    tokenType: data.tokenType,
    hasRefreshSession: data.hasRefreshSession,
  }
}

export interface CardAuthSlice {
  cardAuth: CardAuthState & {
    setActiveAddress: (address: string | undefined) => void
    setTokens: (tokens: CardAuthTokens) => void
    clearTokens: () => void
    hasValidTokens: () => boolean
    isTokenNearExpiry: (secondsBeforeExpiry?: number) => boolean
  }
}

export interface PersistedCardAuthSlice {
  cardAuth: {
    byAddress: Record<string, Pick<PerAddressAuthData, 'hasRefreshSession'>>
  }
}

interface PersistedCardAuthStateLike {
  byAddress?: Record<string, { hasRefreshSession?: boolean } | undefined>
}

const initialState: CardAuthState = {
  activeAddress: null,
  byAddress: {},
  accessToken: null,
  expiresIn: null,
  tokenType: null,
  hasRefreshSession: false,
}

export const initCardAuthSlice: StateCreator<StoreState, [], [], CardAuthSlice> = (set, get) => ({
  cardAuth: {
    ...initialState,

    setActiveAddress: (address: string | undefined) => {
      const key = address?.toLowerCase() ?? null
      if (get().cardAuth.activeAddress === key) return
      const data = key ? get().cardAuth.byAddress[key] ?? emptyAddressData : emptyAddressData
      set((state: StoreState) => ({
        cardAuth: {
          ...state.cardAuth,
          activeAddress: key,
          ...flatFieldsFrom(data),
        },
      }))
    },

    setTokens: (tokens: CardAuthTokens) => {
      const tokenFields = {
        accessToken: tokens.accessToken,
        expiresIn: tokens.expiresIn,
        tokenType: tokens.tokenType,
        hasRefreshSession: true,
      }
      set((state: StoreState) => {
        const addr = state.cardAuth.activeAddress
        if (!addr) return state
        const current = state.cardAuth.byAddress[addr] ?? emptyAddressData
        return {
          cardAuth: {
            ...state.cardAuth,
            ...tokenFields,
            byAddress: {
              ...state.cardAuth.byAddress,
              [addr]: { ...current, ...tokenFields },
            },
          },
        }
      })
    },

    clearTokens: () => {
      const cleared = {
        accessToken: null,
        expiresIn: null,
        tokenType: null,
        hasRefreshSession: false,
      }
      set((state: StoreState) => {
        const addr = state.cardAuth.activeAddress
        if (!addr) {
          return { cardAuth: { ...state.cardAuth, ...cleared } }
        }
        const current = state.cardAuth.byAddress[addr] ?? emptyAddressData
        return {
          cardAuth: {
            ...state.cardAuth,
            ...cleared,
            byAddress: {
              ...state.cardAuth.byAddress,
              [addr]: { ...current, ...cleared },
            },
          },
        }
      })
    },

    hasValidTokens: () => {
      const { cardAuth } = get()
      return isCardAuthValid(cardAuth)
    },

    isTokenNearExpiry: (secondsBeforeExpiry = 10) => {
      const { cardAuth } = get()
      if (!cardAuth.expiresIn) return false
      return cardAuth.expiresIn <= Date.now() + secondsBeforeExpiry * 1000
    },
  },
})

export const persistCardAuthSlice = (state: StoreState): PersistedCardAuthSlice => ({
  cardAuth: {
    byAddress: Object.fromEntries(
      Object.entries(state.cardAuth.byAddress)
        .filter(([, data]) => data.hasRefreshSession)
        .map(([address, data]) => [address, { hasRefreshSession: data.hasRefreshSession }]),
    ),
  },
})

export function restorePersistedCardAuthSlice(
  persistedCardAuth: PersistedCardAuthStateLike | null | undefined,
): Pick<CardAuthState, 'byAddress'> {
  const byAddress = Object.fromEntries(
    Object.entries(persistedCardAuth?.byAddress ?? {})
      .filter((entry): entry is [string, { hasRefreshSession?: boolean }] => entry[1]?.hasRefreshSession === true)
      .map(([address, data]) => [
        address,
        {
          ...emptyAddressData,
          hasRefreshSession: data.hasRefreshSession === true,
        },
      ]),
  )

  return {
    byAddress,
  }
}
